<template>
  <div class="cell-row" :class="header.type">
    <span class="cell-display">
      <template v-if="editing">
        <template v-if="header.type === 'string'">
          <form @submit.prevent="finishCell">
            <k-text-search ref="stringinputedit" v-model="pendingEditSpace"></k-text-search>
          </form>
        </template>
        <template v-if="header.type === 'input-text'">
          <form @submit.prevent="finishCell">
            <k-text-input ref="textinputedit" v-model="pendingEditSpace"></k-text-input>
          </form>
        </template>
        <template v-if="header.type === 'multi-input-selection'">
          <form @submit.prevent="() => {}" class="cell-controls">
            <div v-if="header.options && header.options.length > 0">
              <word-tags v-model="pendingEditSpace" textInputPlaceholder="Type tag here..." :options="header.options"></word-tags>
            </div>
            <div v-else class="no-option-warning">
              <p>
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                There are no options to select
              </p>
            </div>
          </form>
        </template>
        <template v-if="header.type === 'multi-input'">
          <form @submit.prevent="() => {}" class="cell-controls">
            <word-tags v-model="pendingEditSpace" textInputPlaceholder="Type tag here..."></word-tags>
          </form>
        </template>
        <template v-else-if="header.type === 'multi-link-input'">
          <div v-for="(link, index) in entry" :key="index">
            <form v-if="editingArrayElement === index" @submit.prevent="finishCell" class="cell-controls">
              <k-text-input ref="textinputedit" v-model="pendingEditSpace"></k-text-input>
              <button class="btn btn-success k-table-cell-button save" @click="finishCell" title="Save changes" aria-label="Save changes">
                <i class="fas fa-check" aria-hidden="true"></i>
              </button>
            </form>
            <div v-else>
              <a :href="link">{{ link }}</a>
            </div>
          </div>
        </template>
        <template v-else-if="header.type === 'date' || header.type === 'datetime'">
          <k-date class="cell-date" v-model="pendingEditSpace" :type="header.type" :minuteIncrement="header.increment" :timezone="timezone" :minDate="header.minDate"></k-date>
        </template>
        <template v-else-if="header.type === 'time'">
          <k-time v-model="editSpace"></k-time>
        </template>
      </template>
      <template v-else-if="header.type === 'dropdown'">
        <span v-if="header.rowOptions">
          <k-dropdown v-if="header.rowOptions[row._index].length > 0" v-model="editSpace" :options="header.rowOptions[row._index]" :show-search="false" placeholder="select" :key="entry">
          </k-dropdown>
          <span v-else>No selectable options</span>
        </span>
        <k-dropdown v-else v-model="editSpace" :options="header.options" :show-search="false" placeholder="select" :key="entry"></k-dropdown>
      </template>
      <template v-else-if="showDatePlaceHolder">
        <i class="select-date">Select a date</i>
      </template>
      <template v-else-if="header.type === 'action'">
        <div v-html="header.html" class="action-container" @click="header.callback(row, row._index, header)">
        </div>
      </template>
      <template v-else-if="header.type === 'html'">
        <div v-html="header.html(entry, row)">
        </div>
      </template>
      <template v-else-if="header.type === 'multi-input-selection' || header.type === 'multi-input'">
        <span>
          <ul class="k-word-tags-list">
            <li class="k-word-tags-item badge" v-for="group in entry" :key="group">
              {{group}}
            </li>
          </ul>
        </span>
      </template>
      <template v-else-if="header.type === 'multi-link-input'" >
        <div v-for="(link, index) in entry" class="multi-input-cell-control">
          <a :href="link" class="me-auto">{{ link }}</a>
          <div class="cell-controls">
            <button class="btn btn-filters edit-link" @click="editCellElement(index)" title="Edit" aria-label="Edit">
              <i class="fas fa-edit" aria-hidden="true"></i>
            </button>
            <button class="btn btn-filters remove-link"
                    @click="updateCell(editSpace.filter((el, i) => i !== index), true)"
                    title="Delete"
                    aria-label="Delete">
              <i class="fas fa-trash-alt" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div class="multi-input-cell-control">
          <div class="cell-controls">
            <button class="btn btn-filters add-link" @click="addArrayEl" title="Add" aria-label="Add">
              <i class="fas fa-plus" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </template>
      <template v-else-if="header.type === 'boolean'">
        <label class="k-custom-checkbox" :for="'k-checkbox-input-' + row._index + '-' + header.key">
          <input :id="'k-checkbox-input-' + row._index + '-' + header.key"
            type="checkbox"
            class="hidden-checkbox"
            title="checkbox"
            aria-label="checkbox"
            :disabled="!header.editable"
            @click="updateCell(!entry)"
            v-model="editSpace">
          <span class="k-custom-checkbox-box ph-no-capture"></span>
        </label>
      </template>
      <template v-else-if="header.type === 'pill'">
        <k-status-pill
          :type="pillConfig.type"
          :fill="pillConfig.fill"
          :text-color="pillConfig.textColor"
          :status-value="pillConfig.statusValue"
        >
          <template #label v-if="pillConfig.slotContent">
            <div v-html="pillConfig.slotContent"></div>
          </template>
        </k-status-pill>
      </template>
      <template v-else>
        {{display(entry, header)}}
      </template>
    </span>
    <span class="cell-controls" v-if="showEdit(header)">
      <template v-if="editing && header.type !== 'multi-link-input'">
        <button class="btn btn-success k-table-cell-button" @click="finishCell" title="Save changes" aria-label="Save changes">
          <i class="fas fa-check" aria-hidden="true"></i>
        </button>
      </template>
      <template v-else>
        <button v-if="header.type !== 'boolean' && header.type !== 'multi-link-input'" class="btn btn-filters" title="Edit" aria-label="Edit" @click="editCell">
          <i class="fas fa-edit" aria-hidden="true"></i>
        </button>
        <button v-if="!header.required && entry && header.type !== 'multi-link-input'" class="btn btn-filters" title="Delete" aria-label="Delete" @click="updateCell(undefined, true)">
          <i class="fas fa-trash-alt" aria-hidden="true"></i>
        </button>
      </template>
    </span>
  </div>
</template>
<style>
.cell-display,
.cell-display li {
  width: 100%;
  color: var(--kate-type-light) !important;
}

.cell-controls,
.multi-input-cell-control {
  display: inline-flex;
}

.multi-input-cell-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 5px;
}

.k-editable-table-row .cell-row {
  display: flex;
  align-items: center;
}

.k-editable-table-row .cell-row.boolean,
.k-editable-table-row .cell-row.action {
  text-align: center;
}

.select-date {
  color: var(--kate-type-primary);
}

.multi-input-selection {
  align-items: baseline !important;
}

.multi-input-selection .btn-success {
  margin-left: 5px;
}

.multi-input .k-word-tags-item,
.multi-input-selection .k-word-tags-item {
  width: auto !important;
}

.k-editable-table-row .k-text-input {
  margin: 0;
}

.cell-row.input-text .cell-display .field-label:empty {
  display: none;
}

</style>
<style scoped>
.k-word-tags-list {
  list-style: none;
  margin-top: 10px;
  padding-left: 0;
}

.k-table-cell-button.btn-success {
  padding: 8px 12px;
}

.me-auto {
  white-space: nowrap;
}

.k-editable-table-row .cell-row.boolean .k-custom-checkbox input.hidden-checkbox {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 1;
  z-index: 2;
  border: none;
}

.k-editable-table-row .cell-row.boolean .k-custom-checkbox {
  position: relative;
  justify-content: center;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KDate from './k-date-picker.vue';
import KTime from './k-time-picker.vue';
import KDropdown from './k-dropdown.vue';
import KTextSearch from './k-text-search.vue';
import KTextInput from './k-text-input.vue';
import WordTags from '../dashboard/components/word-tags.vue';
import KStatusPill from './k-status-pill.vue';

export default {
  mixins: [TimeMixin],
  components: {
    WordTags,
    'k-time': KTime,
    'k-date': KDate,
    'k-dropdown': KDropdown,
    'k-text-search': KTextSearch,
    'k-text-input': KTextInput,
    'k-status-pill': KStatusPill,
  },
  props: {
    header: {
      type: Object,
    },
    entry: {
      type: [Object, String, Boolean, Date, Number, Array],
    },
    row: {
      type: Object,
    },
    timezone: String,
    preservedEditingState: Object,
  },
  data: () => ({
    editing: false,
    editSpace: undefined,
    addNew: undefined,
    multiEntryEditingIndex: undefined,
    pendingEditSpace: undefined,
    directUpdateCellTypes: ['dropdown', 'boolean', 'action'],
  }),
  beforeMount() {
    this.editSpace = this.entry;
    this.pendingEditSpace = this.entry;

    if (this.preservedEditingState) {
      this.editing = true;
      this.editSpace = this.preservedEditingState.editSpace;
      this.pendingEditSpace = this.preservedEditingState.pendingEditSpace;
      this.multiEntryEditingIndex = this.preservedEditingState.multiEntryEditingIndex;
    }
  },
  watch: {
    editSpace: {
      handler() {
        if (this.directUpdateCell) {
          this.updateCell(this.editSpace);
        }
      },
      deep: true,
    },
    entry: {
      handler() {
        this.editSpace = this.entry;
      },
      deep: true,
    },
    pendingEditSpace: {
      handler(newValue) {
        if (this.editing) {
          this.$emit('update-editing-state', this.header.key, {
            editSpace: this.editSpace,
            pendingEditSpace: newValue,
            multiEntryEditingIndex: this.multiEntryEditingIndex,
          });
        }
      },
      deep: true,
    },
  },

  computed: {
    showDatePlaceHolder() {
      if (this.header.type === 'date' || this.header.type === 'datetime') {
        if (this.entry === null || this.entry === undefined) {
          return true;
        }
      }
      return false;
    },
    editingArrayElement() {
      return this.multiEntryEditingIndex;
    },
    directUpdateCell() {
      return this.directUpdateCellTypes.includes(this.header.type);
    },
    pillConfig() {
      return this.getPillConfig(this.header, this.entry);
    },
  },
  methods: {
    display(obj, header) {
      if ('text' in header) {
        return header.text(obj);
      }
      if (header.type === 'date') {
        return this.parseTimestamp(obj, undefined, 'do MMM yyyy');
      }
      if (header.type === 'datetime') {
        return this.parseTimestamp(obj, undefined, 'do MMM yyyy HH:mm', this.timezone);
      }
      if (header.type === 'time') {
        return obj;
      }
      return obj;
    },
    addArrayEl() {
      this.editCellElement();
      // Make sure it is an array
      if (!this.editSpace) {
        this.editSpace = [];
      }
      this.editSpace.push('');
      this.multiEntryEditingIndex = this.editSpace.length - 1;
      this.updateCell(this.editSpace);
    },
    editCellElement(index) {
      this.editSpace = this.entry;
      this.multiEntryEditingIndex = index;
      this.pendingEditSpace = this.entry ? this.entry[index] : undefined;
      this.editing = true;
    },
    editCell() {
      this.editSpace = this.entry;
      if (!this.editSpace || this.editSpace.length === 0) {
        this.pendingEditSpace = this.header.type === 'multi-input' || this.header.type === 'multi-input-selection' ? [] : undefined;
      } else {
        this.pendingEditSpace = this.editSpace;
      }
      this.editing = true;
      if (this.header.type === 'string') {
        this.$nextTick(() => {
          this.$refs.stringinputedit.focus();
        });
      }
    },
    showEdit(header) {
      if (!header.editable) {
        return false;
      }
      if (typeof header.editable === 'function') {
        return header.editable(this.row);
      }
      return header.type !== 'dropdown' && header.type !== 'boolean';
    },
    update(newRow) {
      this.$emit('update', newRow);
    },
    updateCell(nuval, commit) {
      this.update(nuval);
      if (commit) {
        this.$nextTick(() => {
          this.commitEdits();
        });
      }
    },
    finishCell() {
      this.editing = false;
      if (this.pendingEditSpace != null) {
        if (this.multiEntryEditingIndex != null) {
          this.editSpace[this.multiEntryEditingIndex] = this.pendingEditSpace;
          this.pendingEditSpace = undefined;
        } else {
          this.editSpace = this.pendingEditSpace;
        }
      }
      this.updateCell(this.editSpace);
      this.commitEdits();
      this.$emit('update-editing-state', this.header.key, null);
    },
    commitEdits() {
      this.$emit('commitEdits', this.row);
    },

    getPillConfig(header, entry) {
      const statusValue = header.options?.statusValue ? header.options.statusValue(entry) : this.display(entry, header);
      const headerOptions = header.options || {};

      const getConfigValue = option => {
        if (!option) return '';
        return typeof option === 'function' ? option(statusValue) : option;
      };

      return {
        statusValue: statusValue === null ? '-' : String(statusValue || ''),
        type: getConfigValue(headerOptions.type),
        fill: getConfigValue(headerOptions.fill),
        textColor: getConfigValue(headerOptions.textColor),
        slotContent: getConfigValue(headerOptions.slotContent),
      };
    },
  },
};
</script>
